import React, { useState, createContext, useContext, useEffect } from 'react';
import type { AlertColor } from '@mui/material';
import CustomButton from 'src/core/ui/CustomButton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAlert } from 'src/shared/support/useAlert';
import CustomToastContainer from 'src/shared/support/CustomToastContainer';
import { extractApiPath } from 'src/core/api/cache/idbUtility';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { useAuth } from 'src/shared/auth/useAuth';

export const MessageProvider: React.FC = ({ children }) => {
  const [eventContainers, setEventContainers] = useState<{ [key: string]: EventContainer; }>({});
  const [eventHistory, setEventHistory] = useState<{ [key: string]: EventContainer; }>({});
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<AlertColor | null>(null);
  const { settings, setSetting } = useSettingsContext();
  const showAlert = useAlert();

  const { isAuthenticated } = useAuth() || { isAuthenticated: false };

  const createEventContainer = (id: string, requests: string[], messages: { success: string; failure: string }, settings = { autoClose: AUTO_CLOSE, details: undefined }) => {
    setEventContainers(prev => ({ ...prev, [id]: { requests: requests.reduce((acc, cur) => ({ ...acc, [cur]: { status: 'pending', error: null }, }), {}), messages, settings, }, }));
  };

  const updateMessageStatus = ({ id, requestUrl, status, message = '', logLevel = ['success', 'error'], }: UpdateMessageStatusConfig) => {
    const toastStatus = mapStatusToToastStatus(status);
    if (!logLevel.includes(toastStatus)) return;
    const contextTitle = formatRequestContext(requestUrl || extractApiPath(requestUrl ?? '')) || extractApiPath(requestUrl ?? '');
    const { displayMessage, detailsText } = status === 'success'
      ? { displayMessage: message || 'Operation completed successfully.', detailsText: null }
      : parseApiMessage(message);

    // No id = treat as standalone toast
    if (!id) {
      const tempId = `temp-${Date.now()}${Math.random()}`;
      setEventHistory(prevHistory => ({ ...prevHistory, }));
      const toastContent = (
        <ToastContent contextTitle={contextTitle} displayMessage={toastStatus === 'success' ? displayMessage || 'Operation successful.' : displayMessage || 'An error has occurred.'} detailsText={detailsText} />
      );
      if (toastStatus === 'success' || toastStatus === 'error') {
        const isInteractive = !!detailsText;
        toast[toastStatus](toastContent, { autoClose: AUTO_CLOSE ? AUTO_CLOSE_DURATION : false, closeOnClick: !isInteractive, pauseOnHover: true, });
      } else {
        console.warn('Invalid toast status:', status);
      }
      return;
    }

    // With id = part of a grouped event
    setEventContainers(prev => {
      const newContainers = { ...prev };
      const container = newContainers[id];
      if (container && container.requests[requestUrl]) {
        container.requests[requestUrl] = { status: mapStatusToRequestStatus(status), error: message ?? null, };
      }
      return newContainers;
    });
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkAndUpdateMessage = (id: string) => {
      const container = eventContainers[id];
      if (!container) return;
      const allSucceeded = Object.values(container.requests).every(req => req.status === 'success');
      const anyFailed = Object.values(container.requests).some(req => req.status === 'failure');
      if (allSucceeded || anyFailed) {
        const newMessage = allSucceeded
          ? container.messages.success
          : `${container.messages.failure}. Details: ${Object.values(container.requests).filter(req => req.error).map(req => req.error).join(', ')}`;
        setMessage(newMessage);
        setMessageType(allSucceeded ? 'success' : 'error');
        showAlert(newMessage, allSucceeded ? 'success' : 'error', container.settings);
        setEventHistory(prevHistory => ({ ...prevHistory, [id]: container }));
        setEventContainers(prevContainers => {
          const newContainers = { ...prevContainers };
          delete newContainers[id];
          return newContainers;
        });
      }
    };
    Object.keys(eventContainers).forEach(checkAndUpdateMessage);
  }, [eventContainers, isAuthenticated, showAlert]);

  return (
    <>
      {isAuthenticated && <CustomToastContainer />}{' '}
      <MessageContext.Provider value={{ createEventContainer, updateMessageStatus, message, messageType, }}>{children}</MessageContext.Provider>
    </>
  );
};

export const useMessageContext = () => useContext(MessageContext);

const AUTO_CLOSE = true;

export const AUTO_CLOSE_DURATION = 5000;

export const MessageContext = createContext<MessageContextProps>({ createEventContainer: () => { }, updateMessageStatus: () => { }, message: null, messageType: null, });

const capitalizeWord = (s: string) => s.toLowerCase().split(' ').map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : '')).join(' ');

const formatRequestContext = (requestUrl: string): string => {
  if (!requestUrl) return '';

  let path = requestUrl;

  try {
    const url = new URL(requestUrl);
    path = url.pathname;
  } catch {
    // maybe it's already just a path like /api/...
  }

  // trim query/hash
  path = path.split('?')[0].split('#')[0];

  // remove leading slash
  if (path.startsWith('/')) path = path.slice(1);

  // strip "api/" prefix
  if (path.toLowerCase().startsWith('api/')) {
    path = path.slice(4);
  }

  const segments = path.split('/').map(seg => seg.trim()).filter(Boolean);

  if (!segments.length) return '';

  const formattedSegments = segments.map(seg => capitalizeWord(seg.replace(/-/g, ' ')));

  return formattedSegments.join(' > ');
};

const safeParse = (raw: string | undefined) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const parseApiMessage = (rawMessage?: string): ParsedMessage => {
  if (!rawMessage) {
    return { displayMessage: 'An error has occurred.', detailsText: null, };
  }
  const parsed = safeParse(rawMessage);
  if (parsed && typeof parsed === 'object') {
    const anyParsed = parsed as any;

    // main user-facing message
    const displayMessage =
      typeof anyParsed.Message === 'string' && anyParsed.Message.trim().length
        ? anyParsed.Message.trim()
        : rawMessage;

    // hide ExceptionMessage/StackTrace in details, but make them copyable
    let detailsText: string | null = null;
    if (anyParsed.ExceptionMessage || anyParsed.StackTrace) {
      detailsText = JSON.stringify({ ExceptionMessage: anyParsed.ExceptionMessage, StackTrace: anyParsed.StackTrace, }, null, 2);
    }
    return { displayMessage, detailsText, };
  }

  // not JSON, just return raw
  return { displayMessage: rawMessage, detailsText: null, };
};

const mapStatusToToastStatus = (status: 'success' | 'failure' | 'error') => status === 'success' ? 'success' : 'error';

const mapStatusToRequestStatus = (status: 'success' | 'failure' | 'error'): 'success' | 'failure' => (status === 'success' ? 'success' : 'failure');

const ToastContent: React.FC<ToastContentProps> = ({ contextTitle, displayMessage, detailsText, }) => {
  const [showDetails, setShowDetails] = useState(false);
  const handleCopy = () => {
    const textToCopy = detailsText || displayMessage;
    if (!textToCopy) return;
    try {
      void navigator.clipboard.writeText(textToCopy);
    } catch (e) {
      console.warn('Failed to copy to clipboard', e);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      {contextTitle && (
        <div style={{ fontWeight: 600, marginBottom: 4, }}>{contextTitle}</div>
      )}
      <div>{displayMessage}</div>
      {detailsText && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div><CustomButton title={showDetails ? 'Hide details' : 'Show details'} size="small" variant="text" onClick={() => setShowDetails(prev => !prev)} /></div>
            <div><CustomButton title="Copy details" size="small" variant="text" onClick={handleCopy} /></div>
          </div>
          {showDetails && (
            <pre style={{ marginTop: 8, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12, }}>{detailsText}</pre>
          )}
        </div>
      )}
    </div>
  );
};

interface EventContainer {
  requests: {
    [key: string]: {
      status: 'pending' | 'success' | 'failure';
      error: string | null;
    };
  };
  messages: {
    success: string;
    failure: string;
  };
  settings: {
    autoClose: boolean;
    details?: string;
  };
}

type UpdateMessageStatusConfig = {
  id?: string;
  requestUrl: string;
  status: 'success' | 'failure' | 'error';
  message?: string;
  logLevel?: string[];
};

interface MessageContextProps {
  createEventContainer: (id: string, requests: string[], messages: { success: string; failure: string }, settings?: { autoClose?: boolean; details?: string }) => void;
  updateMessageStatus: (config: UpdateMessageStatusConfig) => void;
  message: string | null;
  messageType: AlertColor | null;
}

type ParsedMessage = {
  displayMessage: string;
  detailsText: string | null;
};

interface ToastContentProps {
  contextTitle: string;
  displayMessage: string;
  detailsText: string | null;
}
